<template>
  <div>
    <Loading :active.sync="loading" :isFullPage="false"></Loading>

    <!-- Confirm Delete Dialog -->
    <ConfirmDialog />

    <!-- List view -->
    <Card v-if="!showForm" class="p-no-shadow-app">
      <template #title>
        <div class="p-d-flex p-jc-between p-ai-center">
          <span>Recepção</span>
          <Button
            v-if="canEdit"
            label="Nova Recepção"
            icon="pi pi-plus"
            class="p-button-sm p-button-success"
            @click="openNewForm"
          />
        </div>
      </template>
      <template #content>
        <DataTable
          :value="recepcoes"
          :loading="loadingTable"
          responsiveLayout="scroll"
          class="p-datatable-sm"
          :autoLayout="true"
          :expandedRows.sync="expandedRows"
          @row-expand="onRowExpand"
          @row-collapse="onRowCollapse"
        >
          <Column :expander="true" style="width: 3rem" />
          <Column field="arrivalDate" header="Data" />
          <Column field="trackingNumber" header="Nº Guia" />
          <Column field="transportMethod" header="Envio">
            <template #body="slotProps">
              {{ slotProps.data.transportMethod }}
              <span v-if="slotProps.data.transportMethod === 'NACEX'" class="p-ml-2 nacex-badge">NACEX</span>
            </template>
          </Column>
          <Column field="tipo" header="Tipo" />
          <Column header="Tracking" v-if="hasNacexRecords">
            <template #body="slotProps">
              <span v-if="slotProps.data.trackingStatus" :class="statusBadgeClass(slotProps.data.trackingStatus)">
                {{ slotProps.data.trackingStatus }}
              </span>
              <span v-else-if="slotProps.data.transportMethod !== 'NACEX'">—</span>
            </template>
          </Column>
          <Column field="scheduledByName" header="Marcado por" />
          <Column header="Ações" style="width: 200px">
            <template #body="slotProps">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-success p-button-text"
                v-tooltip.top="'Editar'"
                @click="openEditForm(slotProps.data)"
              />
              <Button
                v-if="slotProps.data.transportMethod === 'NACEX' && slotProps.data.trackingNumber"
                icon="pi pi-search"
                class="p-button-rounded p-button-info p-button-text"
                v-tooltip.top="'Verificar estado NACEX'"
                :loading="trackingRow === slotProps.data.id"
                @click="trackNacex(slotProps.data)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-danger p-button-text"
                v-tooltip.top="'Eliminar'"
                @click="confirmDelete(slotProps.data)"
              />
            </template>
          </Column>
          <template #expansion="slotProps">
            <div class="expanded-row-content">
              <h5 class="text-primary p-mb-3">
                <i class="pi pi-info-circle p-mr-2"></i>Detalhes da Recepção
              </h5>
              <div class="p-grid p-formgrid p-mb-3">
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Data/Hora de Chegada:</strong> {{ slotProps.data.arrivalDate }} {{ slotProps.data.arrivalTime ? 'às ' + slotProps.data.arrivalTime : '' }}<br>
                    <strong>Técnico:</strong> {{ slotProps.data.scheduledByName || '—' }}<br>
                    <strong>Estado:</strong>
                    <span :class="slotProps.data.received ? 'badge badge-success p-ml-1' : 'badge badge-warning p-ml-1'">
                      {{ slotProps.data.tipo }}
                    </span>
                  </div>
                </div>
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Meio de Transporte:</strong> {{ slotProps.data.transportMethod }}
                    <span v-if="slotProps.data.otherTransportMethod"> ({{ slotProps.data.otherTransportMethod }})</span><br>
                    <strong>Guia Transporte:</strong> {{ slotProps.data.trackingNumber || '—' }}
                  </div>
                </div>
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Estado Físico:</strong> {{ slotProps.data.physicalCondition || 'Sem observações físicas.' }}
                  </div>
                </div>
              </div>

              <!-- Accessories Section -->
              <h5 class="text-primary p-mb-2">
                <i class="pi pi-briefcase p-mr-2"></i>Acessórios Selecionados
              </h5>
              <div class="info-card p-mb-3">
                <div v-if="getSelectedAccessories(slotProps.data).length > 0" class="accessories-summary-grid">
                  <div v-for="acc in getSelectedAccessories(slotProps.data)" :key="acc.code" class="accessory-item">
                    <i class="pi pi-check-circle text-success p-mr-2"></i>
                    <span class="font-bold">{{ acc.name }}</span>
                  </div>
                </div>
                <div v-else class="text-muted">
                  Nenhum acessório selecionado.
                </div>
                <div v-if="slotProps.data.otherAccessories" class="p-mt-3 pt-2" style="border-top: 1px solid #dee2e6;">
                  <strong>Outros Acessórios:</strong> {{ slotProps.data.otherAccessories }}
                </div>
              </div>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>

    <!-- Inline Form -->
    <Card v-if="showForm">
      <template #title>
        <div class="p-d-flex p-jc-between p-ai-center">
          <span>{{ editingId ? 'Editar Recepção' : 'Nova Recepção' }}</span>
          <Button
            icon="pi pi-arrow-left"
            label="Voltar"
            class="p-button-sm p-button-text"
            @click="closeForm"
          />
        </div>
      </template>
      <template #content>
        <div class="p-grid p-formgrid">
          <div class="p-field p-col-12 p-md-4">
            <label>Data de Chegada</label>
            <Calendar
              v-model="form.arrivalDate"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :disabledDays="[0,6]"
              inputClass="p-inputtext-sm"
              class="w-full"
            />
          </div>

          <div class="p-field p-col-12 p-md-3">
            <label>Hora de Cheg.</label>
            <Calendar
              v-model="form.arrivalTime"
              timeOnly
              hourFormat="24"
              :stepMinute="15"
              inputClass="p-inputtext-sm"
              class="w-full"
            />
          </div>

          <div class="p-field p-col-12 p-md-3 p-d-flex p-ai-end p-pb-2">
            <Checkbox inputId="cb-received" v-model="form.received" :binary="true" />
            <label for="cb-received" class="p-ml-2 cb-label" @click="form.received = !form.received">Recepcionado</label>
          </div>

          <div class="p-field p-col-12 p-md-4">
            <label>Meio Transp.</label>
            <Dropdown
              v-model="form.transportMethod"
              :options="meiosTransporte"
              optionLabel="label"
              optionValue="value"
              class="p-inputtext-sm w-full"
              appendTo="body"
            />
          </div>

          <div class="p-field p-col-12 p-md-4">
            <label>Outra</label>
            <InputText v-model="form.otherTransportMethod" class="p-inputtext-sm w-full" />
          </div>

          <div class="p-field p-col-12 p-md-3">
            <label>Técnico</label>
            <Dropdown
              v-model="form.scheduledBy"
              :options="technicians"
              optionLabel="username"
              optionValue="user_id"
              class="p-inputtext-sm w-full"
              appendTo="body"
              placeholder="Selecione"
            />
          </div>

          <div class="p-field p-col-12">
            <label>Guia Transporte</label>
            <InputText v-model="form.trackingNumber" class="p-inputtext-sm w-full" />
          </div>

          <div class="p-field p-col-12 p-md-6">
            <label>Estado Físico</label>
            <Textarea v-model="form.physicalCondition" rows="4" class="p-inputtext-sm w-full" />
          </div>

          <div class="p-field p-col-12 p-md-6">
            <label>OUTROS ACESSORIOS</label>
            <Textarea v-model="form.otherAccessories" rows="4" class="p-inputtext-sm w-full" />
          </div>

          <!-- Checkboxes de acessórios -->
          <div class="p-col-12">
            <div class="acessorios-grid">
              <div
                v-for="acc in accessoryOptions"
                :key="acc.code"
                class="p-d-flex p-ai-center"
              >
                <Checkbox
                  :inputId="'cb-' + acc.code"
                  v-model="form.accessories[acc.code]"
                  :binary="true"
                />
                <label
                  :for="'cb-' + acc.code"
                  class="p-ml-2 cb-label"
                  @click="$set(form.accessories, acc.code, !form.accessories[acc.code])"
                >{{ acc.name }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="p-d-flex p-jc-end p-mt-4">
          <Button
            label="Cancelar"
            icon="pi pi-times"
            class="p-button-text p-mr-2"
            @click="closeForm"
          />
          <Button
            :label="editingId ? 'Atualizar' : 'Guardar'"
            icon="pi pi-check"
            class="p-button-success"
            :loading="loading"
            @click="saveRecepcao"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import PatService from "../../services/pat.service";
import ReceptionAccessoryService from "../../services/reception-accessory.service";
import { MEIOS_TRANSPORTE } from "../../constants/pat-options";
const emptyForm = (accessoryOptions = [], currentUser = null) => {
  const accessories = {};
  accessoryOptions.forEach((acc) => {
    accessories[acc.code] = false;
  });
  return {
    arrivalDate: new Date(),
    arrivalTime: new Date(),
    received: false,
    transportMethod: null,
    otherTransportMethod: "",
    trackingNumber: "",
    physicalCondition: "",
    otherAccessories: "",
    scheduledBy: currentUser ? currentUser.id : null,
    accessories,
  };
};

export default {
  name: "PatReception",
  components: {
    Loading,
  },
  props: {
    patId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingTable: false,
      showForm: false,
      editingId: null,
      form: emptyForm([], this.$store.state.auth.user),
      meiosTransporte: MEIOS_TRANSPORTE,
      technicians: [],
      recepcoes: [],
      expandedRows: [],
      trackingRow: null,
      accessoryOptions: [],
    };
  },
  computed: {
    canEdit() {
      return this.can("managePATs");
    },
    hasNacexRecords() {
      return this.recepcoes.some((r) => r.transportMethod === "NACEX");
    },
  },
  async mounted() {
    this.loading = true;
    await this.loadData();
    this.loading = false;
  },
  methods: {
    async loadData() {
      try {
        this.loadingTable = true;
        const [data, techs, accResponse] = await Promise.all([
          PatService.getReceptions(this.patId),
          PatService.getTechnicians(),
          ReceptionAccessoryService.getAll(),
        ]);

        if (techs) this.technicians = techs;
        this.accessoryOptions = accResponse?.data || accResponse || [];
        this.form = emptyForm(this.accessoryOptions, this.$store.state.auth.user);

        this.recepcoes = data?.data || data || [];
      } catch (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Erro ao carregar recepções.",
          life: 3000,
        });
      } finally {
        this.loadingTable = false;
      }
    },
    openNewForm() {
      this.form = emptyForm(this.accessoryOptions, this.$store.state.auth.user);
      this.editingId = null;
      this.showForm = true;
      this.expandedRows = [];
    },
    openEditForm(row) {
      const defaultAccessories = emptyForm(this.accessoryOptions, this.$store.state.auth.user).accessories;
      this.form = {
        arrivalDate: row.arrivalDate ? new Date(row.arrivalDate) : null,
        arrivalTime: row.arrivalTime ? (() => { const d = new Date(); const [h, m] = (row.arrivalTime).split(':'); d.setHours(+h || 0, +m || 0, 0, 0); return d; })() : null,
        received: row.received || false,
        transportMethod: row.transportMethod,
        otherTransportMethod: row.otherTransportMethod || "",
        trackingNumber: row.trackingNumber || "",
        physicalCondition: row.physicalCondition || "",
        otherAccessories: row.otherAccessories || "",
        scheduledBy: row.scheduledBy,
        accessories: row.accessories
          ? { ...defaultAccessories, ...row.accessories }
          : defaultAccessories,
      };
      this.editingId = row.id;
      this.showForm = true;
      this.expandedRows = [];
    },
    closeForm() {
      this.showForm = false;
      this.editingId = null;
      this.form = emptyForm(this.accessoryOptions, this.$store.state.auth.user);
      this.expandedRows = [];
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    buildPayload() {
      return {
        arrival_date: this.formatDate(this.form.arrivalDate),
        arrival_time: this.form.arrivalTime
          ? `${String(this.form.arrivalTime.getHours()).padStart(2, '0')}:${String(this.form.arrivalTime.getMinutes()).padStart(2, '0')}`
          : null,
        received: this.form.received,
        transport_method: this.form.transportMethod,
        other_transport_method: this.form.otherTransportMethod,
        tracking_number: this.form.trackingNumber,
        physical_condition: this.form.physicalCondition,
        other_accessories: this.form.otherAccessories,
        accessories: this.form.accessories,
        scheduled_by: this.form.scheduledBy,
      };
    },
    async saveRecepcao() {
      try {
        this.loading = true;
        const payload = this.buildPayload();

        if (this.editingId) {
          await PatService.updateReception(this.patId, this.editingId, payload);
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Recepção atualizada com sucesso.",
            life: 3000,
          });
        } else {
          await PatService.createReception(this.patId, payload);
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Recepção criada com sucesso.",
            life: 3000,
          });
        }

        this.closeForm();
        await this.loadData();
      } catch (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Erro ao guardar recepção.",
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    confirmDelete(row) {
      this.$confirm.require({
        message: "Tem a certeza que pretende eliminar esta recepção?",
        header: "Confirmar eliminação",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        acceptLabel: "Eliminar",
        rejectLabel: "Cancelar",
        accept: () => this.deleteRecepcao(row),
      });
    },
    async deleteRecepcao(row) {
      try {
        this.loading = true;
        await PatService.deleteReception(this.patId, row.id);
        this.$toast.add({
          severity: "success",
          summary: "Eliminado",
          detail: "Recepção eliminada com sucesso.",
          life: 3000,
        });
        await this.loadData();
      } catch (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Erro ao eliminar recepção.",
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async trackNacex(row) {
      try {
        this.trackingRow = row.id;
        const result = await PatService.trackReception(this.patId, row.id);
        const trackingStatus = result.status || result.data?.status;
        const receptionIndex = this.recepcoes.findIndex((item) => item.id === row.id);

        if (receptionIndex !== -1) {
          this.$set(this.recepcoes[receptionIndex], "trackingStatus", trackingStatus);
        }

        this.$toast.add({
          severity: "success",
          summary: "Estado NACEX",
          detail: `Estado: ${trackingStatus}`,
          life: 5000,
        });
      } catch (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Erro ao verificar estado NACEX.",
          life: 3000,
        });
      } finally {
        this.trackingRow = null;
      }
    },
    onRowExpand(event) {
      const row = event.data;
      this.expandedRows = [row];
    },
    onRowCollapse(event) {
      this.expandedRows = this.expandedRows.filter((r) => r.id !== event.data.id);
    },
    getSelectedAccessories(row) {
      if (!row.accessories) return [];
      return this.accessoryOptions.filter(acc => row.accessories[acc.code]);
    },
    statusBadgeClass(status) {
      if (!status) return '';
      const s = String(status).toUpperCase();
      if (s === 'DELIVERED' || s === 'COMPLETED' || s.includes('ENTREGUE')) return 'badge badge-success';
      if (s === 'CANCELED' || s.includes('CANCEL')) return 'badge badge-danger';
      if (s === 'CREATED' || s.includes('REGISTAD')) return 'badge badge-info';
      return 'badge badge-warning';
    },
  },
};
</script>

<style scoped>
.acessorios-grid {
  display: grid;
  grid-template-columns: repeat(6, auto);
  gap: 0.5rem 1.5rem;
  padding: 0.5rem 0;
}

.acessorios-grid label {
  cursor: pointer;
}

.cb-label {
  cursor: pointer;
  user-select: none;
}

.w-full {
  width: 100%;
}

.nacex-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #2196F3;
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}
.badge-success { background: #c8e6c9; color: #256029; }
.badge-danger { background: #ffcdd2; color: #c63737; }
.badge-info { background: #b3e5fc; color: #23547b; }
.badge-warning { background: #ffd8b2; color: #805b36; }

.expanded-row-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin: 0.5rem 0;
  padding: 1rem;
}

.text-primary {
  color: #0056b3;
  font-size: 1rem;
  font-weight: 600;
}

.info-card {
  background: white;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 0.9rem;
  line-height: 1.6;
}

.accessories-summary-grid {
  display: grid;
  gap: 0.5rem 1rem;
}

@media (max-width: 768px) {
  .accessories-summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 769px) {
  .accessories-summary-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

.accessory-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.text-success {
  color: #2e7d32;
}

.text-muted {
  color: #9e9e9e;
}

.font-bold {
  font-weight: 600;
}

</style>
